import type { DtoV1Borrower1003BorrowerSummaryAddress } from '~/__generated__/api';

import type { BorrowerAddressData } from '../../../types';

export function clearInitialPrimaryId(
  deletedAddressId: number | null,
  initialPrimaryId: number | null,
): number | null {
  return deletedAddressId === initialPrimaryId ? null : initialPrimaryId;
}

export function getValidAddressId(
  address?: { addressIdentifier?: { addressId?: number } | null } | null,
): number | null {
  const addressId = address?.addressIdentifier?.addressId;

  return typeof addressId === 'number' && addressId > 0 ? addressId : null;
}

export function demotePrimaryToAssociatedAddresses(
  details: { borrowerAssociatedAddresses?: BorrowerAddressData[] | null },
  outgoingPrimary?: BorrowerAddressData | null,
): BorrowerAddressData[] {
  const associatedAddresses = details.borrowerAssociatedAddresses ?? [];
  const outgoingAddressId = getValidAddressId(outgoingPrimary);

  if (!outgoingPrimary || outgoingAddressId === null) {
    return associatedAddresses;
  }

  if (
    associatedAddresses.some(
      (address) => getValidAddressId(address) === outgoingAddressId,
    )
  ) {
    return associatedAddresses;
  }

  return [
    ...associatedAddresses,
    {
      ...outgoingPrimary,
      borrowerAddress: {
        ...outgoingPrimary.borrowerAddress,
        primaryResidence: false,
      },
    },
  ];
}

export function mergePrimaryResidenceIntoHistory(residenceHistoryDetails?: {
  borrowerResidenceHistory?: DtoV1Borrower1003BorrowerSummaryAddress[] | null;
  primaryResidence?: DtoV1Borrower1003BorrowerSummaryAddress | null;
}): DtoV1Borrower1003BorrowerSummaryAddress[] {
  const borrowerResidenceHistory =
    residenceHistoryDetails?.borrowerResidenceHistory ?? [];
  const primaryResidence = residenceHistoryDetails?.primaryResidence;

  if (!primaryResidence) {
    return borrowerResidenceHistory;
  }

  const primaryAddressId = getValidAddressId(primaryResidence);
  const deduplicatedHistory =
    primaryAddressId === null
      ? borrowerResidenceHistory
      : borrowerResidenceHistory.filter(
          (history) => getValidAddressId(history) !== primaryAddressId,
        );

  return [
    {
      ...primaryResidence,
      borrowerAddress: {
        ...primaryResidence.borrowerAddress,
        primaryResidence: true,
      },
    },
    ...deduplicatedHistory,
  ];
}

export function dedupeAssociatedAddressesAgainstPrimary(
  associatedAddresses:
    | DtoV1Borrower1003BorrowerSummaryAddress[]
    | null
    | undefined,
  primaryResidence: DtoV1Borrower1003BorrowerSummaryAddress | null | undefined,
) {
  const primaryAddressId = getValidAddressId(primaryResidence);

  if (primaryAddressId === null) {
    return associatedAddresses;
  }

  return (associatedAddresses ?? []).filter(
    (address) => getValidAddressId(address) !== primaryAddressId,
  );
}
